/**
 * G3 — fingerprint + cooldown for window.error / unhandledrejection logging + Sentry.
 *
 * Policy: at most one customerLogger + Sentry capture per fingerprint per 60s cooldown.
 * The first #185 in a window still logs once (arms render-storm latch via main.tsx);
 * identical fingerprints during an armed render-storm latch are fully suppressed here
 * (no second log/Sentry) while suppressedPriorErrors still accrues for the next allowed log.
 */

import {
  isReactMaximumUpdateDepthError,
  isRenderStormLatched,
} from './renderStormLatch.js';

export const WINDOW_ERROR_DEDUP_COOLDOWN_MS = 60_000;

const lastLoggedAtMs = new Map<string, number>();
const suppressedSinceLastLog = new Map<string, number>();

export function fingerprintWindowError(
  error: Error,
  meta?: Readonly<Record<string, unknown>>,
): string {
  const message = error.message.slice(0, 240);
  const stackHead = (error.stack ?? '').split('\n').slice(0, 2).join('|').slice(0, 240);
  let source = '';
  if (typeof meta?.source === 'string') {
    source = meta.source;
  } else if (typeof meta?.module === 'string') {
    source = meta.module;
  }
  const filename = typeof meta?.filename === 'string' ? meta.filename : '';
  return `${message}::${stackHead}::${source}::${filename}`;
}

export interface ReportDedupedWindowErrorInput {
  readonly error: Error;
  readonly meta: Readonly<Record<string, unknown>>;
  readonly nowMs?: number;
  readonly cooldownMs?: number;
  readonly log: (message: string, error: Error, meta: Readonly<Record<string, unknown>>) => void;
  readonly capture: (error: Error, meta: Readonly<Record<string, unknown>>) => void;
}

/**
 * Log + capture at most once per fingerprint per cooldown window.
 * Emits suppressedPriorErrors count when a burst was coalesced.
 */
export function reportDedupedWindowError(input: ReportDedupedWindowErrorInput): void {
  const cooldownMs = input.cooldownMs ?? WINDOW_ERROR_DEDUP_COOLDOWN_MS;
  const nowMs = input.nowMs ?? Date.now();
  const fingerprint = fingerprintWindowError(input.error, input.meta);
  const stormLatched = isRenderStormLatched();
  if (stormLatched && isReactMaximumUpdateDepthError(input.error)) {
    suppressedSinceLastLog.set(
      fingerprint,
      (suppressedSinceLastLog.get(fingerprint) ?? 0) + 1,
    );
    return;
  }
  const last = lastLoggedAtMs.get(fingerprint);
  if (last !== undefined && nowMs - last < cooldownMs) {
    suppressedSinceLastLog.set(
      fingerprint,
      (suppressedSinceLastLog.get(fingerprint) ?? 0) + 1,
    );
    return;
  }

  const suppressed = suppressedSinceLastLog.get(fingerprint) ?? 0;
  suppressedSinceLastLog.delete(fingerprint);
  lastLoggedAtMs.set(fingerprint, nowMs);

  const meta =
    suppressed > 0
      ? { ...input.meta, suppressedPriorErrors: suppressed }
      : input.meta;

  input.log('Unhandled window error', input.error, meta);
  input.capture(input.error, meta);
}

export function reportDedupedUnhandledRejection(input: ReportDedupedWindowErrorInput): void {
  const cooldownMs = input.cooldownMs ?? WINDOW_ERROR_DEDUP_COOLDOWN_MS;
  const nowMs = input.nowMs ?? Date.now();
  const fingerprint = fingerprintWindowError(input.error, input.meta);
  const stormLatched = isRenderStormLatched();
  if (stormLatched && isReactMaximumUpdateDepthError(input.error)) {
    suppressedSinceLastLog.set(
      fingerprint,
      (suppressedSinceLastLog.get(fingerprint) ?? 0) + 1,
    );
    return;
  }
  const last = lastLoggedAtMs.get(fingerprint);
  if (last !== undefined && nowMs - last < cooldownMs) {
    suppressedSinceLastLog.set(
      fingerprint,
      (suppressedSinceLastLog.get(fingerprint) ?? 0) + 1,
    );
    return;
  }

  const suppressed = suppressedSinceLastLog.get(fingerprint) ?? 0;
  suppressedSinceLastLog.delete(fingerprint);
  lastLoggedAtMs.set(fingerprint, nowMs);

  const meta =
    suppressed > 0
      ? { ...input.meta, suppressedPriorErrors: suppressed }
      : input.meta;

  input.log('Unhandled promise rejection', input.error, meta);
  input.capture(input.error, meta);
}

export function clearWindowErrorDedupForTests(): void {
  lastLoggedAtMs.clear();
  suppressedSinceLastLog.clear();
}
